import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../components/ui/LegalPage';
import COMPANY, { ROUTES } from '../config/company';
import { SMS_CONSENT_TEXT } from '../components/ui/SmsConsentCheckbox';

const B = COMPANY.brand;
const LEGAL = COMPANY.legalName;

/**
 * The SMS program terms. Rendered inside the full Terms & Conditions
 * (anchor #sms-terms) AND as the standalone /sms-terms page, so both URLs
 * quoted in the Telnyx campaign show identical wording.
 */
export const SMS_TERMS_BODY = [
  <p key="a">
    <strong>Program name.</strong> {COMPANY.sms.programName}.
  </p>,
  <p key="p">
    <strong>Program description.</strong> {LEGAL} offers an optional SMS text-message program. You opt in by entering your
    mobile number on our <Link to={ROUTES.smsOptIn}>SMS opt-in page</Link> ({COMPANY.domainDisplay}/sms-opt-in) or{' '}
    <Link to={ROUTES.contact}>Contact form</Link> ({COMPANY.domainDisplay}/contact) and checking one or both separate,
    unchecked-by-default consent boxes:
  </p>,
  { list: [
    <span key="c"><strong>Customer care messages</strong> — customer care replies, reservation confirmations, pickup and return reminders, and account and billing notifications about your trailer rental.</span>,
    <span key="m"><strong>Marketing messages</strong> — trailer availability, rental specials and promotional offers. By checking the marketing box you are opting in to receive marketing text messages from {LEGAL}. {COMPANY.sms.marketingFrequency.charAt(0).toUpperCase() + COMPANY.sms.marketingFrequency.slice(1)}.</span>,
  ] },
  <p key="q"><strong>Exact consent wording.</strong></p>,
  { quote: SMS_CONSENT_TEXT.customerCare },
  { quote: SMS_CONSENT_TEXT.marketing },
  { list: [
    'Consent to receive SMS is optional and is not a condition of purchasing or renting any goods or services. You can submit any form on this website without checking either box.',
    'Message frequency may vary depending on your rental activity.',
    'Message and data rates may apply according to your mobile carrier\u2019s plan.',
    'Reply STOP to any SMS message to opt out at any time. You will receive one final confirmation; after opt-out we will not send further messages unless you opt in again.',
    `Reply HELP to any message for help, call ${COMPANY.phoneDisplay}, or email ${COMPANY.emailDisplay}.`,
    'Your mobile phone number and SMS opt-in consent will not be sold or shared with third parties or affiliates for marketing or promotional purposes.',
    'Carriers are not liable for delayed or undelivered messages. Delivery is subject to effective transmission from your network operator.',
    'You confirm that you are the account holder or have the account holder\u2019s permission for the number you provide, and that you are at least 18 years old.',
  ] },
  <p key="b">
    See our <Link to={ROUTES.privacy}>Privacy Policy</Link> for how we handle the information collected through the
    SMS program, and the <Link to={ROUTES.smsOptIn}>SMS opt-in page</Link> for sample messages.
  </p>,
];

const SECTIONS = [
  ['Acceptance of these terms', [
    `These Terms & Conditions ("Terms") govern your use of the ${COMPANY.domainDisplay} website and the trailer-rental services (together, the "Services") provided by ${LEGAL} ("${B}", "we", "us"). By using the website, submitting a form, opting in to SMS, or renting a trailer you agree to these Terms and to our Privacy Policy. If you do not agree, please do not use the Services.`,
  ]],
  ['SMS terms and conditions', SMS_TERMS_BODY, 'sms-terms'],
  ['Eligibility and carrier requirements', [
    'You must be at least 18 years old and able to enter a binding contract. Trailer rentals are available to motor carriers and owner-operators who provide, before pickup:',
    { list: [
      'A valid, active MC and/or USDOT number.',
      'A certificate of insurance showing liability coverage and trailer interchange / non-owned trailer physical damage coverage of at least $30,000, naming Bitnex Freight LLC as certificate holder and loss payee.',
      'A valid commercial driver’s license for each driver who will pull the trailer.',
      `A signed Trailer Rental Agreement and any additional documents ${B} reasonably requires.`,
    ] },
    'You are responsible for the accuracy of every detail you give us and for keeping any login credentials secure.',
  ]],
  ['Reservations, rates and payment', [
    { list: [
      'Rates quoted on the website are average monthly rates and may change; the rate in your signed rental agreement governs.',
      'Rental fees are due in advance unless otherwise agreed in writing. Late payments may incur additional fees.',
      'There are no mileage fees. Taxes and any optional add-ons are itemized before you sign.',
      'You agree to reimburse Bitnex Freight LLC for any tolls, fines, citations or penalties incurred while the trailer is in your possession.',
    ] },
  ]],
  ['Use and care of the trailer', [
    { list: [
      'Use the trailer only for lawful commercial transportation purposes and in compliance with all federal, state and local regulations.',
      'Keep the trailer in good working condition, perform required pre-trip inspections, and notify us promptly of any damage or mechanical issue.',
      'Trailer maintenance and mechanical repairs are covered by Bitnex Freight LLC during the rental. Trailer tires and tire damage are the responsibility of the lessee.',
      'Do not sublease, lend or transfer possession of the trailer without our prior written consent.',
      'Bitnex Freight LLC retains title to and ownership of the trailer at all times.',
    ] },
  ]],
  ['Return, extension and late return', [
    'The trailer must be returned to the agreed location on the agreed date in the condition received, normal wear and tear excepted. Call or text us before the return date to extend. Late returns may incur additional charges. Damage beyond normal wear is repaired at your cost and may be deducted from any deposit or billed separately.',
  ]],
  ['Liability and insurance', [
    `You assume full responsibility for the trailer and its use during the rental period and must maintain the insurance described above. To the fullest extent permitted by law, ${LEGAL} is not liable for cargo loss or damage, loading issues, operational delays, or any indirect, incidental, special or consequential damages arising from the rental or your use of the Services. Our total liability for any claim is limited to the rental fees you paid for the rental giving rise to the claim. ${B} does not provide cash advances or financial assistance.`,
  ]],
  ['Website use', [
    'You agree not to misuse the website, attempt to gain unauthorized access, scrape content, or submit false information. Content on the website is owned by or licensed to Bitnex Freight LLC and may not be reproduced without permission. Third-party logos shown on the site are trademarks of their respective owners and are used only to identify equipment makes and companies we work with; no endorsement is implied.',
  ]],
  ['Termination', [
    'We may suspend or end a rental or refuse service where these Terms or a rental agreement are breached, where required by law or our insurers, or where we reasonably suspect fraud or misuse. Completed rental periods remain billable.',
  ]],
  ['Governing law', [
    'Any specific governing-law, dispute-resolution or venue provisions for a trailer rental will be set out in the signed rental agreement, subject to applicable law. This website does not change those separate agreement terms.',
  ]],
  ['Changes to these terms', [
    'We may update these Terms from time to time. The "last updated" date at the top of the page shows when they last changed. Continued use of the Services after a change means you accept the updated Terms.',
  ]],
  ['Contact', [
    'Questions about these Terms:',
    { list: [
      
      `Phone: ${COMPANY.phoneDisplay}`,
      `Email: ${COMPANY.emailDisplay}`,
      `${COMPANY.hq.label}: ${LEGAL}, ${COMPANY.hq.line1}, ${COMPANY.hq.line2}`,
    ] },
  ]],
];

export default function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms & Conditions"
      lede="The agreement between you and Bitnex Freight LLC when you use this website, opt in to SMS updates or rent a trailer."
      sections={SECTIONS}
    />
  );
}
